import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import iconPDF from '../../static/refund/pdf_icon.svg';
import { ArrowUpFrontaliere } from '../../theme/frontaliere_icons/arrowup';
import { isFrontaliere } from '../../utils/globalFunctions';

const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'application/pdf': ['.pdf'],
};
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 Mo, aligné avec le backend
const useStyles = makeStyles((theme) => ({
  thumbsContainer: {
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    border: `1px ${theme.palette.secondary.main} solid`,
    height: 'inherit',
    margin: 10,
    ...(isFrontaliere && {
      backgroundColor: 'white',
      borderRadius: '30px !important',
    }),
  },

  thumb: {
    position: 'relative',
    borderRadius: 2,
    margin: 10,
    width: 100,
    height: 100,
    padding: 4,
    boxSizing: 'border-box',
    transition: '.5s ease',
    backfaceVisibility: 'hidden',
    '&:hover img': {
      //image as background
      cursor: 'pointer',
      opacity: 0.3,
    },
    //cross overlay
    '&:hover div': {
      opacity: 1,
    },
    [theme.breakpoints.down('sm')]: {
      width: 70,
      height: 70,
      margin: 5,
    },
  },

  img: {
    opacity: 1,
    display: 'block',
    width: '100%',
    height: '100%',
  },

  middle: {
    transition: '.5s ease',
    cursor: 'pointer',
    opacity: 0,
    position: 'absolute',
    top: '50%',
    left: '50%',
    color: 'red',
    transform: 'translate(-50%, -50%)',
    '-ms-transform': 'translate(-50%, -50%)',
    textAlign: 'center',
    zIndex: 100,
  },
}));

export default function Dropzone(props) {
  const { t } = useTranslation('account');
  const classes = useStyles();
  const [files, setFiles] = props.files;
  const [rejectionMsg, setRejectionMsg] = useState(null);

  const buildRejectionMessage = (rejections) => {
    if (!rejections?.length) return null;
    const reasons = new Set();
    rejections.forEach(({ file, errors }) => {
      errors.forEach((err) => {
        if (err.code === 'file-invalid-type') {
          reasons.add(
            `« ${file.name} » : type non supporté. Formats acceptés : PDF, JPG, PNG, HEIC.`,
          );
        } else if (err.code === 'file-too-large') {
          reasons.add(
            `« ${file.name} » : fichier trop volumineux (max 25 Mo).`,
          );
        } else if (err.code === 'file-too-small') {
          reasons.add(`« ${file.name} » : fichier vide.`);
        } else {
          reasons.add(`« ${file.name} » : ${err.message}`);
        }
      });
    });
    return Array.from(reasons).join(' ');
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    minSize: 1,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles, fileRejections) => {
      const validFiles = acceptedFiles.filter(
        (file) => file && file.size > 0,
      );
      const emptyFromAccepted = acceptedFiles
        .filter((file) => !file || file.size <= 0)
        .map((file) => ({
          file,
          errors: [{ code: 'file-too-small', message: 'Empty file' }],
        }));
      const allRejections = [...fileRejections, ...emptyFromAccepted];

      setRejectionMsg(buildRejectionMessage(allRejections));

      if (validFiles.length === 0) return;

      const previews = validFiles.map((file) =>
        Object.assign(file, {
          preview:
            file.type === 'application/pdf'
              ? iconPDF
              : URL.createObjectURL(file),
        }),
      );

      setFiles(files ? files.concat(previews) : previews);
    },
  });

  const removeFile = (file) => {
    let removedArray = files.filter(function (el) {
      return el !== file;
    });
    setFiles(removedArray);
  };

  const thumbs =
    files.length > 0 ? (
      files.map((file) => (
        <div className={classes.thumb} key={file.name}>
          <img
            src={file.preview}
            className={classes.img}
            alt=""
            onClick={() => removeFile(file)}
          />
          <div className={classes.middle}>
            <span
              style={{ fontSize: '1.8em' }}
              onClick={() => removeFile(file)}
            >
              X
            </span>
          </div>
          <div
            style={{
              fontSize: '0.9em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.name}
          </div>
        </div>
      ))
    ) : (
      <Stack
        spacing={2}
        alignItems={'center'}
        justifyContent="center"
      >
        {isFrontaliere && (
          <ArrowUpFrontaliere
            sx={{ width: '44px', height: '44px' }}
          />
        )}
        <Typography>Glissez et déposez vos documents ici</Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center', maxWidth: 380 }}
        >
          Formats acceptés : PDF, JPG, PNG, HEIC. Taille max&nbsp;:
          25&nbsp;Mo par fichier.
        </Typography>
      </Stack>
    );

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files &&
        files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files],
  );

  return (
    <div
      {...getRootProps({ className: 'dropzone' })}
      style={{
        height: '100%',
        width: '100%',
      }}
    >
      <input {...getInputProps()} />
      <Box
        className={classes.thumbsContainer}
        sx={{
          ...(files.length === 0 && {
            alignItems: 'center',
            justifyContent: 'center',
          }),
        }}
      >
        {thumbs}
      </Box>
      {rejectionMsg && (
        <Alert
          severity="warning"
          onClose={() => setRejectionMsg(null)}
          sx={{ mx: 1, mt: 1, color: 'black' }}
        >
          {rejectionMsg}
        </Alert>
      )}
      <Stack
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: { xs: '20px', sm: '40px' },
        }}
      >
        <Button
          variant="contained"
          style={{
            fontSize: '18px',
            ...(isFrontaliere && {
              borderRadius: '100px',
              width: { xs: 'auto', lg: '300px' },
            }),
          }}
          color="button"
          onClick={open}
        >
          {t('dropzone')}
        </Button>
      </Stack>
    </div>
  );
}
